import React, { useMemo } from "react";
import { type PackListingView } from "../../types/state";
import styles from "./PricingSection.module.scss";
import { PackFreeLabel } from "components/packs/packsList/packFreeLabel/PackFreeLabel";
import { PackRequestButton } from "components/packs/PackRequestButton/PackRequestButton";
import { useHasEnablePackPermissions } from "features/Licensing/Packs/hooks/useHasEnablePackPermissions";
import RemovePackButton from "features/Licensing/Packs/Listings/removePackButton/RemovePackButton";
import { getRequestLabelText, isFreePricingType } from "../../utils/packUtils";
import { type AccountTypeEnum, HIDDEN_ACCOUNT_TYPES } from "../../../../../interfaces/Account";
import { useFeatureFlag } from "../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../featureFlags";

export interface PricingSectionProps {
  pack: PackListingView;
  hasPurchasePackAddon: boolean;
  onPackRequest: () => void;
  onPackTrialRequest: () => void;
  onPackRemove: () => void;
  accountTypeId: AccountTypeEnum;
  parentAccountTypeId: AccountTypeEnum | null;
}

const formatPriceDisplay = (price: number, isFree: boolean): string => {
  const zero = "0.00";
  if (isFree) {
    return `$${zero}`;
  }
  return `$${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || zero}`;
};

export const PricingSection: React.FC<PricingSectionProps> = ({
  pack,
  hasPurchasePackAddon,
  onPackRequest,
  onPackTrialRequest,
  onPackRemove,
  accountTypeId,
  parentAccountTypeId,
}) => {
  const {
    packPricingTypeId,
    pricingSettings,
    isOwn,
    isPurchased,
    isExpired,
    isRequested,
    isArchived,
    isFromTemplate,
    isPrivate,
    isTrial,
  } = pack;
  const hasEnablePackPermissions = useHasEnablePackPermissions();
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const additionalPurchase = additionalPurchaseEnabled && isPurchased && !isExpired && !isTrial;

  const requestButtonLabel = useMemo(() => {
    if (additionalPurchase) {
      return hasEnablePackPermissions ? "Buy More Licenses" : "Request More Licenses";
    }
    return getRequestLabelText(packPricingTypeId, hasPurchasePackAddon);
  }, [additionalPurchase, packPricingTypeId, hasPurchasePackAddon, hasEnablePackPermissions]);

  const isFree = useMemo(() => isFreePricingType(packPricingTypeId), [packPricingTypeId]);

  const requestButtonDisabled =
    (!additionalPurchaseEnabled && isPurchased && !isExpired && !isTrial) ||
    isArchived ||
    isPrivate ||
    isFromTemplate ||
    isRequested ||
    (!additionalPurchaseEnabled && !hasEnablePackPermissions && isPurchased);
  const displayPrice = formatPriceDisplay(pricingSettings?.pricePerUserPerMonth || 0, isFree);

  return (
    <>
      <div className={styles.pricingHeader}>
        <h3 className={styles.title}>Pricing</h3>
        <PackFreeLabel isFree={isFree} />
      </div>

      {!HIDDEN_ACCOUNT_TYPES.includes(accountTypeId) &&
        (!parentAccountTypeId || !HIDDEN_ACCOUNT_TYPES.includes(parentAccountTypeId)) && (
          <div className={styles.priceDisplay}>
            <span className={styles.price}>{displayPrice}</span>
            {!isFree && <span className={styles.priceUnit}>Per user per month</span>}
          </div>
        )}
      {!isOwn && isFree && isPurchased && (
        <RemovePackButton onClick={onPackRemove} disabled={isFromTemplate || isPrivate} />
      )}
      {!isOwn && (!isPurchased || !isFree) && (
        <PackRequestButton
          className={styles.requestButton}
          triggerText={requestButtonLabel}
          onTrialClick={onPackTrialRequest}
          onRequestClick={onPackRequest}
          startTrialLabel={`Start Free ${pricingSettings?.trialPeriod?.name || ""} Trial`}
          isMultiple={!!pricingSettings?.isTrialAllowed && !isPurchased}
          disabled={requestButtonDisabled}
          hasPurchasePermission={hasEnablePackPermissions}
          hasPurchasePackAddon={hasPurchasePackAddon}
        />
      )}

      {isFree ? (
        <p className={styles.note}>
          <a href={envtConfig.termsOfServicesUrl} target="_blank" rel="noreferrer">
            Terms and conditions
          </a>{" "}
          apply
        </p>
      ) : (
        <p className={styles.note}>
          Volume discounts may be applicable. Please{" "}
          <a href={"mailto:billing@brainstorminc.com"} target="_blank" rel="noreferrer">
            contact us
          </a>{" "}
          for more information.
        </p>
      )}
    </>
  );
};
